import React from 'react';
import { useTheme } from 'styled-components';

import { IAppTheme } from '../../styles/IAppTheme';

export interface AiIconProps {
  /** Icon height in pixels - width is calculated proportionally */
  height?: number;
  /** Inner sparkle color - accepts hex color or CSS variable */
  innerColor?: string;
  /** Outer circle background color - accepts hex color or CSS variable */
  outerColor?: string;
  /** Additional CSS class names */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * AiIcon Component
 * 
 * AI sparkle/star icon used as avatar fallback when no custom avatar is provided.
 * Pure SVG implementation with no external dependencies (no FontAwesome).
 * 
 * Features:
 * - Three-star design (large main star + two small accent stars)
 * - Circular background container
 * - Configurable inner and outer colors
 * - Supports CSS variables for theming
 * - Accessible (aria-hidden for decorative use)
 * - Matches the design from online-community's AiSparkAvatar
 * 
 * @example
 * ```tsx
 * <AiIcon 
 *   height={18} 
 *   innerColor="var(--ai-avatar-secondary-color)"
 *   outerColor="var(--ai-avatar-primary-color)" 
 * />
 * ```
 */
export const AiIcon: React.FC<AiIconProps> = ({
  height = 18,
  innerColor,
  outerColor,
  className = '',
  style = {},
  onClick,
  disabled = false,
}) => {
  const theme = useTheme() as IAppTheme;
  const sparkleColor = innerColor || '#ffffff';
  const circleColor = outerColor || theme.color.primary || '#007bff';

  // Calculate proportional dimensions based on height
  const largeStar = 18 * (height / 24);
  const secondaryStarsWidth = 9 * (height / 24);

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      return;
    }
    e.stopPropagation();
    onClick?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || !onClick) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClick(e as unknown as React.MouseEvent);
    }
  };

  return (
    <div 
      className={`ai-spark-icon ${className}`.trim()}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-disabled={disabled}
      style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '36px',
        minWidth: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: circleColor,
        opacity: disabled ? 0.5 : 1,
        cursor: onClick && !disabled ? 'pointer' : 'default',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Main large star */}
        <svg width={largeStar} height={largeStar} viewBox="0 0 18 18" fill={sparkleColor}>
          <path d="M9.61406 0.435938C9.49219 0.16875 9.225 0 8.93438 0C8.64375 0 8.37656 0.16875 8.25469 0.435938L5.78438 5.78437L0.435938 8.25C0.16875 8.37187 0 8.63906 0 8.93438C0 9.22969 0.16875 9.49219 0.435938 9.61406L5.78438 12.0844L8.25 17.4281C8.37187 17.6953 8.63906 17.8641 8.92969 17.8641C9.22031 17.8641 9.4875 17.6953 9.60938 17.4281L12.0797 12.0797L17.4281 9.60938C17.6953 9.4875 17.8641 9.22031 17.8641 8.92969C17.8641 8.63906 17.6953 8.37187 17.4281 8.25L12.0844 5.78437L9.61406 0.435938Z"></path>
        </svg>

        {/* Two small accent stars on the right */}
        <svg width={secondaryStarsWidth} height={height} viewBox="0 0 9 24" fill={sparkleColor}>
          <path d="M0.351562 3.99375L3 3L3.99375 0.351562C4.07344 0.140625 4.275 0 4.5 0C4.725 0 4.92656 0.140625 5.00625 0.351562L6 3L8.64844 3.99375C8.85938 4.07344 9 4.275 9 4.5C9 4.725 8.85938 4.92656 8.64844 5.00625L6 6L5.00625 8.64844C4.92656 8.85938 4.725 9 4.5 9C4.275 9 4.07344 8.85938 3.99375 8.64844L3 6L0.351562 5.00625C0.140625 4.92656 0 4.725 0 4.5C0 4.275 0.140625 4.07344 0.351562 3.99375ZM0.351562 18.9938L3 18L3.99375 15.3516C4.07344 15.1406 4.275 15 4.5 15C4.725 15 4.92656 15.1406 5.00625 15.3516L6 18L8.64844 18.9938C8.85938 19.0734 9 19.275 9 19.5C9 19.725 8.85938 19.9266 8.64844 20.0062L6 21L5.00625 23.6484C4.92656 23.8594 4.725 24 4.5 24C4.275 24 4.07344 23.8594 3.99375 23.6484L3 21L0.351562 20.0062C0.140625 19.9266 0 19.725 0 19.5C0 19.275 0.140625 19.0734 0.351562 18.9938Z"></path>
        </svg>
      </div>
    </div>
  );
};

export default AiIcon;
